import { Popover } from '@headlessui/react';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { ReferenceFieldLink } from 'components/padi-ui/header/types';
import { useAccount } from 'frontastic';
import { useFormat } from 'helpers/hooks/useFormat';
import useGetUserInfo from 'helpers/hooks/useGetUserInfo';
import { deleteCognitoCookies } from 'helpers/padi/cognito';
import { DeleteSloSession } from 'helpers/padi/slo';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { ProjectConfig } from 'types/project-config';

type Props = {
  projectConfig: ProjectConfig;
  userMenu?: ReferenceFieldLink[];
};

const LoggedIn = (props: Props) => {
  const projectConfig = props.projectConfig;
  const userMenu = props.userMenu;
  const router = useRouter();
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { logout } = useAccount();
  const { locale } = useParams();
  const pathname = usePathname();
  const { userData } = useGetUserInfo();

  const handleLogout = () => {
    const sub = userData?.sub;
    logout()
      .then(() => {
        if (sub) DeleteSloSession(sub);
      })
      .then(() => deleteCognitoCookies())
      .then(() => {
        Object.keys(localStorage)
          .filter((key) => !key.startsWith('padi'))
          .map((key) => localStorage.removeItem(key));
      })
      .then(() => (pathname.endsWith('courses/') ? window.location.reload() : router.push(`/${locale}/courses`)));
  };

  const padiDomains = projectConfig?.padiDomains;
  const [learningSiteUrl, setlearningSiteUrl] = useState<string>('');
  useEffect(() => {
    setlearningSiteUrl(`${padiDomains?.learning}/account` || '');
  }, [learningSiteUrl, padiDomains?.learning]);

  return (
    <div data-id="account-loggedin-container" className="w-235 p-14">
      {userMenu && userMenu.length ? (
        <>
          {userMenu?.map((link, index) => (
            <div key={`link-${index}-${link?.label}`} data-id={`account-menu-link-wrapper-${index}`}>
              <Link
                data-id={`account-menu-link-${index}`}
                link={link?.url}
                padi-analytics={link?.dataAnalytics}
                className="mt-10 block w-fit"
              >
                <Typography data-id={`account-menu-link-text-${index}`} className="text-primary-black hover:underline">
                  {link?.label}
                </Typography>
              </Link>
            </div>
          ))}
          <div data-id="account-logout-wrapper" onClick={handleLogout}>
            <div data-id="account-logout-button" className="mt-10 block w-fit cursor-pointer">
              <Typography data-id="account-logout-text" className="text-primary-black hover:underline">
                {formatAccountMessage({ id: 'sign.out', defaultMessage: 'Sign out' })}
              </Typography>
            </div>
          </div>
        </>
      ) : (
        <>
          <Popover.Button data-id="account-my-account-button" className="w-full">
            {learningSiteUrl && (
              <Link data-id="account-my-account-link" link={learningSiteUrl} className="mt-10 block w-fit">
                <Typography data-id="account-my-account-text" className="text-primary-black hover:underline">
                  {formatAccountMessage({ id: 'my.account', defaultMessage: 'My Account' })}
                </Typography>
              </Link>
            )}
          </Popover.Button>

          <Popover.Button data-id="account-logout-button-wrapper" className="mt-32 block w-full pb-10">
            <div data-id="account-logout-fallback" onClick={handleLogout} className="w-fit">
              <Typography data-id="account-logout-fallback-text" className="text-16 text-primary-black hover:underline">
                {formatAccountMessage({ id: 'sign.out', defaultMessage: 'Sign out' })}
              </Typography>
            </div>
          </Popover.Button>
        </>
      )}
    </div>
  );
};

export default LoggedIn;
